import type { DataMode224, ReportAudience224, ServiceRow224 } from '../domain/partnerConsole224';

export interface ConsoleOverview224 {
  generatedAt: string;
  storage: string;
  schemaVersion: number;
  tenants: { total: number; active: number; onboarding: number; suspended: number; byMode: Record<string, number> };
  accounts: { users: number; families: number; children: number; sessions: number };
  services: ServiceRow224[];
  audit: { stored: number; limit: number; droppedCount: number; oldestKeptAt: string };
  backups: { stored: number; lastAt: string };
}

export interface ConsoleTenant224 {
  id: string; name: string; kind: string; dataMode: DataMode224; status: string;
  organizations: number; classrooms: number;
  seats: { seatLimit: number; used: number; remaining: number; over: boolean };
  onboarding: Record<string, { done: boolean }>;
  onboardingComplete: boolean;
  activationRefusal: string;
  restrictedFeatures: string[];
}

export interface ConsoleReport224 {
  audience: ReportAudience224; dataMode: DataMode224; scope: string; generatedAt: string;
  totals?: { tenants: number; children: number; activeChildren: number; classrooms: number; seatsUsed: number; seatsContracted: number };
  tenants?: { tenantId: string; name: string; children: number; activeChildren: number }[];
}

async function get<T>(path: string): Promise<T | null> {
  try {
    const response = await fetch(path, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    // The console is an operator tool: an unreachable server must render as
    // "cannot read" rather than as an empty, believable-looking dashboard.
    return null;
  }
}

export const fetchConsoleOverview224 = () => get<ConsoleOverview224>('/api/admin/console/overview');
export const fetchConsoleTenants224 = () => get<{ tenants: ConsoleTenant224[] }>('/api/console/tenants');
export const fetchConsoleReport224 = (audience: ReportAudience224, dataMode: DataMode224) =>
  get<ConsoleReport224>(`/api/console/reports?audience=${audience}&dataMode=${dataMode}`);

/** Saves which features a demo tenant may not use. The server is the enforcer. */
export async function saveRestrictedFeatures224(tenantId: string, restrictedFeatures: string[], csrf: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/admin/console/tenants/${tenantId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(csrf ? { 'X-MRM-CSRF': csrf } : {}) },
      body: JSON.stringify({ restrictedFeatures }),
    });
    return response.ok;
  } catch {
    return false;
  }
}
